import { useEffect, useRef } from 'react'
import supabase from '../supabaseClient'

const OAUTH_RETURN_EVENTS = new Set(['SIGNED_IN', 'INITIAL_SESSION'])

export function isPortalOAuthReturnEvent(event) {
  return OAUTH_RETURN_EVENTS.has(event)
}

/**
 * Route portal login pages after OAuth return, password sign-in, or an
 * existing session on mount. Supabase v2 emits INITIAL_SESSION (not
 * SIGNED_IN) when parsing tokens from the URL hash after Google/Microsoft
 * redirect — without this, users stay stuck on /…/login#.
 */
export function usePortalLoginRedirect(onSession, { onSignedOut, onStart, onEnd } = {}) {
  const onSessionRef = useRef(onSession)
  const onSignedOutRef = useRef(onSignedOut)
  const onStartRef = useRef(onStart)
  const onEndRef = useRef(onEnd)
  const routingRef = useRef(false)

  useEffect(() => {
    onSessionRef.current = onSession
    onSignedOutRef.current = onSignedOut
    onStartRef.current = onStart
    onEndRef.current = onEnd
  })

  useEffect(() => {
    const run = async (session) => {
      if (!session?.user || routingRef.current) return
      routingRef.current = true
      onStartRef.current?.()
      try {
        await onSessionRef.current(session)
      } finally {
        routingRef.current = false
        onEndRef.current?.()
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          onSignedOutRef.current?.()
          return
        }
        if (isPortalOAuthReturnEvent(event) && session) {
          await run(session)
        }
      },
    )

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) run(session)
    })

    return () => subscription.unsubscribe()
  }, [])
}
